import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { getExt } from '@bridebook/toolbox/src/getExt';
import { getImageDimensionsFromUrl, IS3File } from '@bridebook/ui';
import {
  ReviewSupplierForm,
  IReviewSupplierFormProps,
} from '@bridebook/web/app-shared/components/review-supplier-form';
import {
  addSupplierFeedback,
  formatReviewFormError,
  validateReviewFormFields,
} from '@bridebook/web/app-shared/lib/reviews/utils';
import { ReviewForm, ReviewFormFields } from '@bridebook/web/lib/types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import validate from '@bridebook/web/lib/validate';
import { FullMobileModalV2 } from '@bridebook/web/components/common/full-mobile-modal-v2/full-mobile-modal-v2';
import { Recaptcha } from '@bridebook/web/components/recaptcha/recaptcha';
import { useCaptcha } from '@bridebook/web/lib/utils/hooks/use-captcha';

const defaultReviewForm: Omit<ReviewForm, 'fields'> = {
  error: null,
  showError: false,
  uploadPhotoSuccess: false,
  recaptchaComplete: false,
};

const defaultReviewFields: ReviewFormFields = {
  weddingDate: '',
  from: 'happyCouple',
  message: '',
  name: '',
  title: '',
  email: '',
  stars: 0,
  photo: {},
};

interface IProps {
  supplier: ISupplier;
  onCloseForm: () => void;
  onSaveReview(review: ReviewForm): void;
  onError(): void;
}

const ReviewSupplierModalInner = ({ supplier, onCloseForm, onSaveReview, onError }: IProps) => {
  const { t } = useTranslation();
  const isMobile = useSelector(getIsMobile);
  const { getCaptchaToken } = useCaptcha();

  // Placeholder for the review form state
  const [reviewForm, setReviewForm] = useState<Omit<ReviewForm, 'fields'>>(defaultReviewForm);

  // Placeholder for the review form fields
  const [formFields, setFormFields] = useState<ReviewFormFields>(defaultReviewFields);

  // Hide review form error handler
  const onHideReviewFormError: IReviewSupplierFormProps['onHideReviewFormError'] =
    useCallback(() => {
      setReviewForm((prev) => ({ ...prev, error: null, showError: false }));
    }, []);

  // Complete review form data
  const review = useMemo<ReviewForm>(
    () => ({ ...reviewForm, fields: formFields }),
    [reviewForm, formFields],
  );

  const _onSaveReview = useCallback(async () => {
    try {
      const captchaToken = await getCaptchaToken('review_submit');
      // Captcha is gated by the token now (verified server-side), so pass `true`.
      await validateReviewFormFields(review.fields, true, validate);
      // Clear error if form is valid
      setReviewForm((prev) => ({ ...prev, error: null, showError: false }));
      await addSupplierFeedback(supplier, review.fields, undefined, captchaToken);
      onSaveReview(review);
      setReviewForm(defaultReviewForm);
      setFormFields(defaultReviewFields);
    } catch (e) {
      const error = formatReviewFormError(e, t);
      setReviewForm((prev) => ({ ...prev, error, showError: true }));
      onError();
    }
  }, [getCaptchaToken, onError, onSaveReview, review, supplier, t]);

  // Handles photo upload
  const onUploadFinish: IReviewSupplierFormProps['onUploadFinish'] = useCallback(async (photo) => {
    const photoData = await getPhotoMetaData(photo);
    setFormFields((prev) => ({ ...prev, photo: { ...prev.photo, ...photoData } }));
  }, []);

  // On change handler for the review form fields, sets the form fields state
  const onChange: IReviewSupplierFormProps['onChange'] = useCallback((e) => {
    setReviewForm((prev) => ({ ...prev, error: null, showError: false }));
    setFormFields((prev) => ({ ...prev, [e.target.name as string]: e.target.value }));
  }, []);

  // Sets the review form rating
  const onSetReviewFormRating: IReviewSupplierFormProps['onSetReviewFormRating'] = useCallback(
    (stars) => {
      setFormFields((prev) => ({ ...prev, stars }));
    },
    [],
  );

  return (
    <FullMobileModalV2 id="review-supplier-modal" show={true} onClose={onCloseForm}>
      <ReviewSupplierForm
        supplier={supplier}
        reviewsPage
        reviewForm={review}
        reviewFormShow
        isMobile={isMobile}
        onChange={onChange}
        onSetReviewFormRating={onSetReviewFormRating}
        onSaveReview={_onSaveReview}
        onHideReviewFormError={onHideReviewFormError}
        onUploadFinish={onUploadFinish}
      />
    </FullMobileModalV2>
  );
};

// The reCAPTCHA provider must be an ancestor of the component calling `useCaptcha`,
// so it wraps the inner modal from the outside (basic pages have no global provider).
export const ReviewSupplierModal = (props: IProps) => (
  <Recaptcha>
    <ReviewSupplierModalInner {...props} />
  </Recaptcha>
);

const getPhotoMetaData = async (photo: IS3File) => {
  const ext = getExt(photo.filename);
  const public_id = photo.filename.replace(`.${ext}`, '');
  const imgixURL = `${IMAGES_BASE_URL}/${public_id}.${ext}`;
  const result = await getImageDimensionsFromUrl(imgixURL);
  const { width, height } = result;

  return {
    portrait: height >= width,
    public_id,
    ext,
    imgixURL,
  };
};
